import React from "react";
import { useParams } from "react-router-dom";
import StepWizard from "components/StepWizard";
import queryString from "query-string";
import moment from "moment";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { chain, filter, find, get, keyBy, map, omit, transform } from "lodash";
import { withApollo } from "@apollo/client/react/hoc";
import type { WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { AiOutlineCalendar } from "react-icons/ai";
import { lockedUserProfilesSelector } from "store/selectors/user";
import type { RootState } from "types/state";
import {
  createTopOffContributions,
  getPayrollLineItems,
} from "actions/payrollActions";
import {
  currentPayrolls,
  getAllEmployerAccounts,
  getEmployerGroup,
  getLinkedEmployees,
  getUsersForGroup,
} from "actions/employerActions";

import ReviewExisting from "./ReviewExisting";
import ReviewApprovals from "./ReviewApprovals";
import Summary from "./Summary";
import StepInfo from "components/StepInfo";
import IconSpinner from "components/IconSpinner";
import "./payrollWorkflow.scss";

const stepToLabels: Record<string | number, { subheader: string }> = {
  1: {
    subheader:
      "Have any of these people left the company, gone on leave, or have no hours for the period?",
  },
  2: {
    subheader: "These are all the system changes you need to review.",
  },
  3: {
    subheader: "Here's the summary.",
  },
};

const steps = [
  {
    index: 1,
    label: "Review Existing",
  },
  {
    index: 2,
    label: "Approve Changes",
  },
  {
    index: 3,
    label: "Submit",
  },
];

interface UserWhoLeftRaw {
  employeeId: string;
  userProfile: Record<string, unknown>;
  contribution: { amount: number };
}

function buildUsersWhoLeft(users: Record<string, UserWhoLeftRaw>) {
  return map(users, (user) => {
    return {
      isUserWhoLeft: true,
      employeeId: user.employeeId,
      userProfile: user.userProfile,
      userNewContributionAmount: 0,
      userCurrentContributionAmount: user.contribution.amount,
    };
  });
}

interface OwnProps {
  groupId?: string;
  // react-step-wizard injects these at runtime; the parent <StepWizard>
  // does not pass them explicitly, so they are optional here.
  stepName?: string;
  goToNamedStep?: (name: string) => void;
  nextStep?: () => void;
  previousStep?: () => void;
  currentStep?: number;
  totalSteps?: number;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const groupId = ownProps.groupId;

  let { usersWhoLeftPlan } = state.payroll;

  return {
    groupId,
    // state.payroll.usersWhoLeftPlan is loosely typed (Record<string, unknown>)
    // in RootState; cast at this boundary to the shape buildUsersWhoLeft reads.
    usersWhoLeftPlan: buildUsersWhoLeft(
      usersWhoLeftPlan as unknown as Record<string, UserWhoLeftRaw>
    ),
    linkedEmployees: state.employer.linkedEmployees,
    lockedUserProfiles: lockedUserProfilesSelector(state),
    pendingContributionChanges: state.payroll.pendingContributionChanges,
    contributionsWithNoChange: state.payroll.contributionsWithNoChange,
    payroll: state.employer.currentPayrolls,
  };
};

const mapDispatchToProps = {
  getLinkedEmployees,
  getEmployerGroup,
  getUsersForGroup,
  getAllEmployerAccounts,
  createTopOffContributions,
  getPayrollLineItems,
  currentPayrolls,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  skippedContributions: Record<string, unknown>;
  approvedContributions: Record<string, unknown>;
  keepPrior: Record<string, unknown>;
  removedEmployees: Record<string, unknown>;
  activeStep: string | number;
  isLoading: boolean;
  group: Record<string, unknown>;
  // written in initial state but never read; kept to mirror the original
  payroll: Record<string, unknown>;
  payPeriodId: string;
}

// The sibling step components (ReviewExisting / ReviewApprovals / Summary) are
// being converted in the same batch and each declares its own stricter, mutually
// inconsistent entry/group types (e.g. group.name is optional in some, required
// in others; userProfile fields are `| null` in one and `| undefined` in
// another). The loosely-typed parent state below holds the runtime objects those
// children consume; each is bridged at the JSX boundary with a per-prop cast
// (the same `as unknown as` convention the children use in their own
// mapStateToProps) so the shared child types are never widened. These local
// mirror types replicate just enough of each child's contract for the cast.
// Mirrors of ReviewExisting.Employee (group.name optional)
type ReviewExistingDict = Record<
  string,
  {
    id: string;
    employeeId: string;
    userProfile: {
      firstName: string | null;
      lastName: string | null;
      lastFourOfSsn: string | null;
    };
  }
>;
type GroupOptionalName = { id: string; name?: string };
// Mirrors of ReviewApprovals.ChangeRequest
type ChangeRequestMirror = {
  employeeId: string;
  userProfile: {
    firstName?: string;
    lastName?: string;
    lastFourOfSsn?: string;
  };
};
type ChangeRequestDict = Record<string, ChangeRequestMirror>;
// Mirrors of Summary.ContributionEntry / ContributionDict / group
type ContributionEntryMirror = { employeeId: string };
type ContributionDictMirror = Record<string, ContributionEntryMirror>;
type GroupRequiredName = { id: string; name: string };

export class PayrollWorkflow extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const queryParams = queryString.parse(window.location.search);
    const payPeriodId = get(queryParams, "payPeriodId", "") as string;

    this.state = {
      skippedContributions: {},
      approvedContributions: {},
      keepPrior: {},
      removedEmployees: {},
      activeStep: "1",
      isLoading: true,
      group: {},
      payroll: {},
      payPeriodId,
    };
  }

  async componentDidMount() {
    if (!this.state.payPeriodId) {
      this.props.push("/dashboard/contributions");
    }

    await Promise.all([
      this.props.getLinkedEmployees(this.props.client as ApolloClient<object>),
      this.props
        .getEmployerGroup(
          this.props.client as ApolloClient<object>,
          this.props.groupId!
        )
        .then((result) => {
          // result is a SUCCESS|FAILURE action union; the original code
          // reads `.data` assuming the success branch — preserved as-is.
          const { data: group } = result as { data: Record<string, unknown> };
          this.setState({ group });
        }),
      this.props.getUsersForGroup(
        this.props.client as ApolloClient<object>,
        this.props.groupId!
      ),
      this.props.getAllEmployerAccounts(
        this.props.client as ApolloClient<object>
      ),
      this.props
        .createTopOffContributions(
          this.props.client as ApolloClient<object>,
          this.props.groupId!,
          this.state.payPeriodId
        )
        .then(() =>
          this.props.getPayrollLineItems(
            this.props.client as ApolloClient<object>,
            this.props.groupId!,
            this.state.payPeriodId
          )
        ),
      // @ts-expect-error pre-existing bug: currentPayrolls requires (client, companyId)
      // but is called with only client here; companyId is undefined at runtime. Preserved as-is.
      this.props.currentPayrolls(this.props.client as ApolloClient<object>),
    ]);

    this.setState({
      isLoading: false,
    });
  }

  _onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  // The four selection dicts (skipped, removed, keepPrior, approved) are
  // mutually exclusive at the user level. Each setter prunes the others so
  // the latest action wins — e.g. marking someone Skipped after they were
  // Keep Prior removes them from keepPrior, so the payload built in Summary
  // can't double-count them in both contributionsToProcess and
  // skippedContributions (which would trip the backend totals check).
  _setSkipped = (skippedIds: Record<string, unknown>) => {
    const empIds = Object.keys(skippedIds);
    this.setState({
      skippedContributions: skippedIds,
      keepPrior: omit(this.state.keepPrior, empIds),
      approvedContributions: omit(this.state.approvedContributions, empIds),
      removedEmployees: omit(this.state.removedEmployees, empIds),
    });
  };

  _setRemoved = (removedIds: Record<string, unknown>) => {
    const empIds = Object.keys(removedIds);
    this.setState({
      removedEmployees: removedIds,
      keepPrior: omit(this.state.keepPrior, empIds),
      approvedContributions: omit(this.state.approvedContributions, empIds),
      skippedContributions: omit(this.state.skippedContributions, empIds),
    });
  };

  _setKeepPrior = (keptIds: Record<string, unknown>) => {
    const empIds = Object.keys(keptIds);
    this.setState({
      keepPrior: keptIds,
      skippedContributions: omit(this.state.skippedContributions, empIds),
      removedEmployees: omit(this.state.removedEmployees, empIds),
      approvedContributions: omit(this.state.approvedContributions, empIds),
    });
  };

  _setApproved = (approvedIds: Record<string, unknown>) => {
    const empIds = Object.keys(approvedIds);
    this.setState({
      approvedContributions: approvedIds,
      skippedContributions: omit(this.state.skippedContributions, empIds),
      removedEmployees: omit(this.state.removedEmployees, empIds),
      keepPrior: omit(this.state.keepPrior, empIds),
    });
  };

  _getDateSection = () => {
    const payPeriodId = this.state.payPeriodId;

    const commonDateFormat = "MMM Do";

    const payPeriod = find(
      this.props.payroll,
      (payroll: Record<string, unknown>) => payroll.payPeriodId === payPeriodId
    );

    const start = get(payPeriod, "payPeriodStart") as
      | moment.MomentInput
      | undefined;
    const end = get(payPeriod, "payPeriodEnd") as
      | moment.MomentInput
      | undefined;

    const period = `${moment(start).format(commonDateFormat)} - ${moment(
      end
    ).format(commonDateFormat)}`;

    return (
      <div className="payroll-calendar">
        <AiOutlineCalendar />
        <span className="payroll-period">Period: {period}</span>
      </div>
    );
  };

  _getUsersToExcludeFromSummary = () => {
    const userIdsToExclude = transform(
      this.props.linkedEmployees as Array<{ employeeId: string }>,
      (acc: string[], emp) => {
        const empId = emp.employeeId;

        if (
          !this.state.removedEmployees[empId] &&
          !this.state.skippedContributions[empId] &&
          !this.state.approvedContributions[empId] &&
          !this.state.keepPrior[empId]
        ) {
          acc.push(empId);
        }
        return acc;
      },
      []
    );

    return userIdsToExclude;
  };

  _getLockedContributions = () => {
    const linkedEmployeesInCurrentGroup = filter(
      this.props.linkedEmployees,
      (employee) => {
        const employeeGroupId = get(employee, "groupWithSchedule.group.id");
        return employeeGroupId === this.props.groupId;
      }
    );

    const linkedEmployeeIdsMap = keyBy(
      linkedEmployeesInCurrentGroup,
      "employeeId"
    );

    return chain(
      this.props.lockedUserProfiles as Record<
        string,
        { id: string } & Record<string, unknown>
      >
    )
      .filter((user) => Boolean(linkedEmployeeIdsMap[user.id]))
      .map((user) => {
        const pendingContribution = find(
          this.props.pendingContributionChanges as Record<
            string,
            Record<string, unknown>
          >,
          {
            employeeId: user.id,
          }
        ) as { userCurrentContributionAmount: number } | undefined;
        const existingContribution = find(
          this.props.contributionsWithNoChange as Record<
            string,
            Record<string, unknown>
          >,
          {
            employeeId: user.id,
          }
        ) as { amount: number } | undefined;
        if (existingContribution) {
          return {
            userProfile: { ...user },
            isLockedUser: true,
            userNewContributionAmount: 0,
            userCurrentContributionAmount: existingContribution.amount,
          };
        } else if (pendingContribution) {
          return {
            userProfile: { ...user },
            isLockedUser: true,
            userNewContributionAmount: 0,
            userCurrentContributionAmount:
              pendingContribution.userCurrentContributionAmount,
          };
        }
      })
      .value();
  };

  render() {
    if (this.state.isLoading) {
      return <IconSpinner centered />;
    }

    const lockedUserContributions = this._getLockedContributions();
    const usersToExclude = this._getUsersToExcludeFromSummary();
    const activeSectionLabels = stepToLabels[this.state.activeStep];
    return (
      <div className="mega-container" id="payroll-workflow">
        <section className="page-title-wrap">
          <article className="text-cell">
            <div className="subtext-and-calendar">
              <p className="page-subtext">{activeSectionLabels.subheader}</p>
              {this._getDateSection()}
            </div>
            <StepInfo steps={steps} activeStepNumber={this.state.activeStep} />
          </article>
        </section>
        <div className="main-content">
          <StepWizard
            onStepChange={this._onStepChange}
            isLazyMount={true}
            transitions={{
              enterRight: "",
              enterLeft: "",
              exitRight: "",
              exitLeft: "",
            }}
          >
            <ReviewExisting
              skippedContributions={
                this.state.skippedContributions as unknown as ReviewExistingDict
              }
              removedEmployees={
                this.state.removedEmployees as unknown as ReviewExistingDict
              }
              stepName="existing"
              group={this.state.group as unknown as GroupOptionalName}
              setSkipped={this._setSkipped}
              setRemoved={this._setRemoved}
            />
            <ReviewApprovals
              stepName="approvals"
              skippedContributions={this.state.skippedContributions}
              removedEmployees={this.state.removedEmployees}
              group={this.state.group as unknown as GroupOptionalName}
              setKeepPrior={this._setKeepPrior}
              setApproved={this._setApproved}
              keptPriorContributions={
                this.state.keepPrior as unknown as ChangeRequestDict
              }
              approvedContributions={
                this.state.approvedContributions as unknown as ChangeRequestDict
              }
              usersWhoLeftPlan={
                this.props.usersWhoLeftPlan as unknown as ChangeRequestMirror[]
              }
              lockedUserContributions={
                lockedUserContributions as unknown as ChangeRequestMirror[]
              }
              lockedEmployeeIds={map(lockedUserContributions, "userProfile.id")}
              payPeriodId={this.state.payPeriodId}
            />
            <Summary
              skippedContributions={
                this.state
                  .skippedContributions as unknown as ContributionDictMirror
              }
              removedEmployees={
                this.state.removedEmployees as unknown as ContributionDictMirror
              }
              keptPriorContributions={
                this.state.keepPrior as unknown as ContributionDictMirror
              }
              approvedContributions={
                this.state
                  .approvedContributions as unknown as ContributionDictMirror
              }
              stepName="summary"
              group={this.state.group as unknown as GroupRequiredName}
              payPeriodId={this.state.payPeriodId}
              usersWhoLeftPlan={
                this.props
                  .usersWhoLeftPlan as unknown as ContributionEntryMirror[]
              }
              usersToExclude={usersToExclude}
              lockedUserContributions={
                lockedUserContributions as unknown as ContributionEntryMirror[]
              }
            />
          </StepWizard>
        </div>
      </div>
    );
  }
}

const ConnectedPayrollWorkflow = connector(withApollo<Props>(PayrollWorkflow));

const PayrollWorkflow_WithParams = (props: OwnProps) => {
  const { id: groupId } = useParams();
  return <ConnectedPayrollWorkflow {...props} groupId={groupId} />;
};

export default PayrollWorkflow_WithParams;
